"use client";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Tabs, Typography, Button, Space } from "antd";
import PhotoGrid from "./PhotoGrid";
import PhotoGridButtons from "@/app/components/PhotoGridButtons";
import { usePhotoLikes } from "../hooks/usePhotoLikes";
import { getDeviceId } from "@/lib/deviceId";

interface PhotoCategory {
  key: string;
  label: string;
}

interface PhotoGalleryProps {
  initialPhotos: any[];
  initialNextCursor: string | null;
  initialCategories?: PhotoCategory[];
  pageSize?: number;
  /** Brand shown first (server-rendered). Defaults to Eddie's Edition. */
  initialBusiness?: string;
  /** Category key shown first (server-rendered). Defaults to All. */
  initialCategory?: string;
}

const DEFAULT_TABS: PhotoCategory[] = [{ key: "all", label: "All" }];

const BUSINESS_TABS = [
  { key: "eddies", label: "Eddie's Edition" },
  { key: "edition-studio", label: "Edition Studio" },
];

const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  initialPhotos,
  initialNextCursor,
  initialCategories,
  pageSize = 24,
  initialBusiness = "eddies",
  initialCategory = "all",
}) => {
  // The initial layout is "AUTO": CSS media queries decide columns/heights at
  // first paint (mobile = multiple/3-up, desktop = single/4-up) so server and
  // client render identically with no JS-driven swap or flicker. Once the user
  // picks a layout via PhotoGridButtons we switch to that explicit choice.
  const [grid, setGrid] = useState<"AUTO" | "SINGLE" | "MULTIPLE">("AUTO");

  const [business, setBusiness] = useState<string>(initialBusiness);
  const [categories, setCategories] = useState<PhotoCategory[]>(
    initialCategories && initialCategories.length > 0
      ? initialCategories
      : DEFAULT_TABS,
  );
  const [activeTab, setActiveTab] = useState<string>(initialCategory);
  const [scrolledOnce, setScrolledOnce] = useState(false);

  // data state (already filtered by backend)
  const [photos, setPhotos] = useState<any[]>(initialPhotos);
  const [nextCursor, setNextCursor] = useState<string | null>(
    initialNextCursor,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const canLoadMore = !!nextCursor;

  const likes = usePhotoLikes(photos, business);

  const fetchPhotosPage = useCallback(
    async (opts: { business: string; category: string; cursor?: string }) => {
      const qs = new URLSearchParams({
        limit: String(pageSize),
        category: opts.category,
        business: opts.business,
      });
      if (opts.cursor) qs.set("cursor", opts.cursor);
      const deviceId = getDeviceId();
      if (deviceId) qs.set("deviceId", deviceId);
      const res = await fetch(`/api/photos?${qs.toString()}`, {
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as { items: any[]; nextCursor: string | null };
    },
    [pageSize],
  );

  const loadFirstPage = useCallback(
    async (nextBusiness: string, category: string) => {
      setLoading(true);
      setError(null);
      try {
        const json = await fetchPhotosPage({
          business: nextBusiness,
          category,
        });
        setPhotos(json.items ?? []);
        setNextCursor(json.nextCursor ?? null);
      } catch (e: any) {
        setError(e?.message ?? "Failed to load");
      } finally {
        setLoading(false);
      }
    },
    [fetchPhotosPage],
  );

  const loadCategories = useCallback(async (nextBusiness: string) => {
    try {
      const res = await fetch(
        `/api/photos/categories?business=${encodeURIComponent(nextBusiness)}`,
        { cache: "no-store" },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setCategories(
        Array.isArray(json.items) && json.items.length
          ? json.items
          : DEFAULT_TABS,
      );
    } catch {
      setCategories(DEFAULT_TABS);
    }
  }, []);

  const handleBusinessChange = useCallback(
    (value: string) => {
      if (value === business) return;
      setBusiness(value);
      setActiveTab("all");
      loadCategories(value);
      loadFirstPage(value, "all");
    },
    [business, loadCategories, loadFirstPage],
  );

  const handleTabChange = useCallback(
    (key: string) => {
      setActiveTab(key);
      loadFirstPage(business, key);
    },
    [business, loadFirstPage],
  );

  const loadMore = useCallback(async () => {
    if (!nextCursor || loading) return;
    setLoading(true);
    setError(null);
    try {
      const json = await fetchPhotosPage({
        business,
        category: activeTab,
        cursor: nextCursor,
      });
      setPhotos((prev) => [...prev, ...(json.items ?? [])]);
      setNextCursor(json.nextCursor ?? null);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load more");
    } finally {
      setLoading(false);
    }
  }, [nextCursor, loading, business, activeTab, fetchPhotosPage]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setScrolledOnce(true);
            loadMore();
          }
        }
      },
      { rootMargin: "600px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <div className="photo-gallery">
      <Tabs
        activeKey={business}
        items={BUSINESS_TABS}
        onChange={handleBusinessChange}
        tabBarStyle={{ marginBottom: 0 }}
      />
      <Tabs
        className="photo-category-tabs"
        activeKey={activeTab}
        items={categories}
        onChange={handleTabChange}
        tabBarStyle={{ marginBottom: 8 }}
      />
      <div className="photo-layout-toolbar">
        <PhotoGridButtons value={grid} onClick={setGrid} />
      </div>

      <PhotoGrid photos={photos} grid={grid} changeUrl={true} likes={likes} />

      <div ref={sentinelRef} style={{ height: 1 }} />

      <Space
        orientation="vertical"
        size="middle"
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          padding: 16,
        }}
      >
        {loading && <Typography.Text>Loading…</Typography.Text>}
        {error && <Typography.Text type="danger">{error}</Typography.Text>}
        {!canLoadMore && !loading && (
          <Typography.Text type="secondary">
            You reached the end.
          </Typography.Text>
        )}
        {canLoadMore && !loading && scrolledOnce && (
          <Button onClick={loadMore}>Load more</Button>
        )}
      </Space>
    </div>
  );
};

export default PhotoGallery;
